import React, { useState } from "react";
import styled from "@emotion/styled";
import TooltipWrapper from "../../../components/general/TooltipWrapper";
import {
    BillingConfig,
    BILLING_FIELD_DESCRIPTIONS,
    ROUNDING_METHOD_LABELS,
    RoundingMethod,
    DEFAULT_BILLING_CONFIG,
} from "../../../models/revenue/BillingConfig";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    color_variants_ocean_light_1,
} from "../../../constants/colors";

interface BillingConfigEditorProps {
    config: BillingConfig;
    onChange: (config: BillingConfig) => void;
    title?: string;
    onCopy?: () => void;
    onPaste?: (config: BillingConfig) => void;
    onReset?: () => void;
    compact?: boolean;
    clipboardConfig?: BillingConfig | null;
}

const infoIcon = "\u24D8"; // Unicode circled info

const BillingConfigEditor: React.FC<BillingConfigEditorProps> = ({
    config,
    onChange,
    title,
    onCopy,
    onPaste,
    onReset,
    compact,
    clipboardConfig,
}) => {
    const [isExpanded, setIsExpanded] = useState(!compact);
    const [inputValues, setInputValues] = useState<Record<string, string>>({});

    const handleNumericChange = (field: keyof BillingConfig, value: string) => {
        // Allow typing - store the raw input value
        setInputValues({ ...inputValues, [field]: value });
        
        // Allow empty string
        if (value === "") {
            onChange({ ...config, [field]: 0 });
            return;
        }
        
        // Allow partial decimals like "5.", "0.", "12."
        if (value.endsWith(".")) {
            // Don't update the config yet, just allow typing
            return;
        }
        
        // Validate and parse the number
        const num = parseFloat(value);
        if (!isNaN(num) && num >= 0) {
            onChange({ ...config, [field]: num });
        }
        // If invalid, don't update the config (but we already stored the input)
    };

    const getDisplayValue = (field: keyof BillingConfig): string => {
        // If we have a pending input value, use that
        if (inputValues[field] !== undefined) {
            return inputValues[field];
        }
        // Otherwise show the config value
        return String(config[field] as number);
    };

    const handleRoundingChange = (value: string) => {
        onChange({ ...config, roundingMethod: value as RoundingMethod });
    };

    const renderField = (
        field: keyof BillingConfig,
        label: string,
        prefix?: string,
        suffix?: string
    ) => {
        if (field === "roundingMethod") {
            return (
                <FieldRow key={field}>
                    <FieldLabel>
                        <TooltipWrapper text={BILLING_FIELD_DESCRIPTIONS[field]} place="right" maxWidth={300}>
                            <span>{label} <InfoIcon>{infoIcon}</InfoIcon></span>
                        </TooltipWrapper>
                    </FieldLabel>
                    <SelectInput
                        value={config.roundingMethod}
                        onChange={(e) => handleRoundingChange(e.target.value)}
                    >
                        {Object.entries(ROUNDING_METHOD_LABELS).map(([key, lbl]) => (
                            <option key={key} value={key}>{lbl}</option>
                        ))}
                    </SelectInput>
                </FieldRow>
            );
        }

        return (
            <FieldRow key={field}>
                <FieldLabel>
                    <TooltipWrapper text={BILLING_FIELD_DESCRIPTIONS[field]} place="right" maxWidth={300}>
                        <span>{label} <InfoIcon>{infoIcon}</InfoIcon></span>
                    </TooltipWrapper>
                </FieldLabel>
                <InputWrapper>
                    {prefix && <InputPrefix>{prefix}</InputPrefix>}
                    <NumberInput
                        type="text"
                        inputMode="decimal"
                        value={getDisplayValue(field)}
                        onChange={(e) => handleNumericChange(field, e.target.value)}
                        onBlur={() => {
                            // Clear the input value cache on blur to sync with config
                            setInputValues({ ...inputValues, [field]: undefined });
                        }}
                        hasPrefix={!!prefix}
                        hasSuffix={!!suffix}
                    />
                    {suffix && <InputSuffix>{suffix}</InputSuffix>}
                </InputWrapper>
            </FieldRow>
        );
    };

    return (
        <Wrapper>
            {title && (
                <Header onClick={() => compact && setIsExpanded(!isExpanded)}>
                    <Title>{title}</Title>
                    <HeaderActions>
                        {onCopy && (
                            <ActionButton onClick={(e) => { e.stopPropagation(); onCopy(); }} title="Copy billing config to clipboard">
                                Copy
                            </ActionButton>
                        )}
                        {onPaste && clipboardConfig && (
                            <ActionButton onClick={(e) => { e.stopPropagation(); onPaste(clipboardConfig); }} title="Paste billing config from clipboard">
                                Paste
                            </ActionButton>
                        )}
                        {onReset && (
                            <ResetActionButton onClick={(e) => { e.stopPropagation(); onReset(); }} title="Reset to defaults">
                                Reset
                            </ResetActionButton>
                        )}
                        {compact && <ExpandIcon>{isExpanded ? "\u25B2" : "\u25BC"}</ExpandIcon>}
                    </HeaderActions>
                </Header>
            )}

            {isExpanded && (
                <FieldsGrid>
                    {renderField("aiMinuteRate", "AI Minute Rate", "$")}
                    {renderField("postTransferMinuteRate", "Post-Transfer Minute Rate", "$")}
                    {renderField("minutesIncluded", "Minutes Included", undefined, "min")}
                    {renderField("callRate", "Per-Call Rate", "$")}
                    {renderField("callsIncluded", "Calls Included")}
                    {renderField("roundingMethod", "Rounding Method")}
                    {renderField("flatMonthlyFee", "Flat Monthly Fee", "$")}
                    {renderField("containedCallRate", "Contained Call Rate", "$")}
                    {renderField("nonContainableCallRate", "Non-Containable Call Rate", "$")}
                    {renderField("orderPlacedRate", "Order Placed Rate", "$")}
                </FieldsGrid>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: ${color_shades_lightest};
    cursor: pointer;
    user-select: none;
    border-bottom: 1px solid ${color_shades_light};
`;

const Title = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${color_text_default};
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`;

const ActionButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 3px 10px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;

    &:hover {
        opacity: 0.9;
    }
`;

const ResetActionButton = styled(ActionButton)`
    background: ${color_colors_decline};
`;

const ExpandIcon = styled.span`
    font-size: 10px;
    color: ${color_text_light};
    margin-left: 4px;
`;

const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding: 12px 14px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const FieldRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const FieldLabel = styled.label`
    font-size: 12px;
    font-weight: 500;
    color: ${color_text_light};
`;

const InfoIcon = styled.span`
    font-size: 11px;
    color: ${color_colors_ocean};
    cursor: help;
`;

const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    overflow: hidden;

    &:focus-within {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px ${color_variants_ocean_light_1};
    }
`;

const InputPrefix = styled.span`
    padding: 6px 8px;
    background: ${color_shades_lightest};
    color: ${color_text_light};
    font-size: 13px;
    border-right: 1px solid ${color_shades_dark};
`;

const InputSuffix = styled.span`
    padding: 6px 8px;
    background: ${color_shades_lightest};
    color: ${color_text_light};
    font-size: 13px;
    border-left: 1px solid ${color_shades_dark};
`;

const NumberInput = styled.input<{ hasPrefix?: boolean; hasSuffix?: boolean }>`
    flex: 1;
    padding: 6px 8px;
    border: none;
    font-size: 14px;
    color: ${color_text_default};
    outline: none;
    min-width: 60px;
`;

const SelectInput = styled.select`
    padding: 6px 8px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 14px;
    color: ${color_text_default};
    background: ${color_defaults_white};
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px ${color_variants_ocean_light_1};
    }
`;

export default BillingConfigEditor;
